import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { functions } from '../firebase';
import { T } from './Translate';

const SupportAssistantContext = createContext(null);

const GREETING = <T>Hola, soy el asistente de Notiline. Puedo buscarte ofertas, eventos, autos, propiedades o locales para hacer pedidos, explicarte cómo usar la app o pasarte con una persona del equipo.</T>;

// Asistente IA de toda la app (Qwen, ver functions/aiAssistant.js). Se manda
// el historial de la conversación (solo los textos) y la pantalla actual.
export const SupportAssistantProvider = ({ children }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', text: GREETING }]);
  const [loading, setLoading] = useState(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const openAssistant = useCallback(() => setIsOpen(true), []);
  const closeAssistant = useCallback(() => setIsOpen(false), []);

  const sendMessage = useCallback(async (text) => {
    if (!text || !text.trim()) return;

    const history = [...messagesRef.current, { role: 'user', text }]
      .filter((m) => typeof m.text === 'string')
      .map((m) => ({ role: m.role, content: m.text }));
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      const callAssistant = functions.httpsCallable('aiAssistant', { timeout: 300000 });
      const result = await callAssistant({
        messages: history,
        page: location.pathname,
        mode: location.pathname.startsWith('/support') ? 'support' : 'app',
      });
      const data = result?.data || {};
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: data.reply || <T>No pude responder en este momento. Intentalo de nuevo.</T>,
          links: data.links || [],
          handoff: !!data.handoff,
        },
      ]);
    } catch (error) {
      console.error('Error calling AI assistant:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: error?.code === 'functions/resource-exhausted'
            ? error.message
            : <T>Tuve un problema para responder. Si querés, te paso con una persona del equipo.</T>,
          handoff: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [location.pathname]);

  const value = {
    isOpen,
    openAssistant,
    closeAssistant,
    messages,
    sendMessage,
    loading,
  };

  return (
    <SupportAssistantContext.Provider value={value}>
      {children}
    </SupportAssistantContext.Provider>
  );
};

export const useSupportAssistant = () => {
  const ctx = useContext(SupportAssistantContext);
  if (!ctx) {
    throw new Error('useSupportAssistant debe usarse dentro de un SupportAssistantProvider');
  }
  return ctx;
};

export default SupportAssistantContext;
